import { useState } from "react";
import { Image, Plus, Sparkles, Tag, Trash2 } from "lucide-react";
import type { PaletteRecord } from "../../lib/database";
import { PaletteSearchPanel } from "../PaletteSearchPanel";
import { PaletteHarmonyGenerator } from "./PaletteHarmonyGenerator";
import { useAlert } from '@ascend/alert';

type CreationMode = "idle" | "harmony";

interface PaletteListProps {
  palettes: PaletteRecord[];
  currentPalette: PaletteRecord | null;
  paletteName: string;
  createError: string | null;
  onPaletteNameChange: (name: string) => void;
  onCreateErrorChange: (error: string | null) => void;
  onSelect: (palette: PaletteRecord) => void;
  onCreate: () => void;
  onCreateFromHarmony: (colors: string[], autoName: string) => void;
  onDelete: (id: string) => void;
  onFilteredPalettesChange: (filtered: PaletteRecord[]) => void;
  contextHex?: string;
}

export function PaletteList({
  palettes,
  currentPalette,
  paletteName,
  createError,
  onPaletteNameChange,
  onCreateErrorChange,
  onSelect,
  onCreate,
  onCreateFromHarmony,
  onDelete,
  onFilteredPalettesChange,
  contextHex,
}: PaletteListProps) {
  const [creationMode, setCreationMode] = useState<CreationMode>("idle");
  const { confirm } = useAlert();

  return (
    <div className="lg:col-span-1 space-y-4">
      <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-4">
        <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-3">
          Create Palette
        </h3>

        {/* Multi-mode creation buttons */}
        <div className="flex gap-2 mb-3">
          <button
            type="button"
            onClick={onCreate}
            className="flex-1 px-3 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors text-sm font-medium flex items-center justify-center gap-1.5"
            aria-label="Quick create palette"
            title="Create an empty palette"
          >
            <Plus className="w-3.5 h-3.5" />
            Quick
          </button>
          <button
            type="button"
            onClick={() =>
              setCreationMode(creationMode === "harmony" ? "idle" : "harmony")
            }
            className={`flex-1 px-3 py-2 rounded-lg transition-colors text-sm font-medium flex items-center justify-center gap-1.5 ${
              creationMode === "harmony"
                ? "bg-purple-600 hover:bg-purple-700 text-white"
                : "bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 text-gray-700 dark:text-gray-300"
            }`}
            aria-label="Create palette from harmony"
            title="Generate a palette from color harmonies"
          >
            <Sparkles className="w-3.5 h-3.5" />
            Harmony
          </button>
          <a
            href="/extract"
            className="flex-1 px-3 py-2 bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 text-gray-700 dark:text-gray-300 rounded-lg transition-colors text-sm font-medium flex items-center justify-center gap-1.5 no-underline"
            title="Extract palette from an image"
          >
            <Image className="w-3.5 h-3.5" />
            Image
          </a>
        </div>

        {/* Optional name input */}
        <div className="flex gap-2">
          <input
            type="text"
            value={paletteName}
            onChange={(e) => {
              onPaletteNameChange(e.target.value);
              if (createError) onCreateErrorChange(null);
            }}
            placeholder="Optional name…"
            className="flex-1 px-3 py-1.5 border border-gray-300 dark:border-gray-600 rounded-lg text-xs bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
          />
        </div>
        {createError && (
          <p className="text-red-500 text-xs mt-1">{createError}</p>
        )}

        {/* Harmony generator */}
        {creationMode === "harmony" && (
          <div className="mt-3">
            <PaletteHarmonyGenerator
              onCreatePalette={(colors, autoName) => {
                onCreateFromHarmony(colors, autoName);
                setCreationMode("idle");
              }}
              onCancel={() => setCreationMode("idle")}
              initialColor={contextHex || "#3b82f6"}
            />
          </div>
        )}
      </div>

      <PaletteSearchPanel
        onFilteredPalettesChange={onFilteredPalettesChange}
        allPalettes={palettes}
      />

      <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-4">
        <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-3">
          Saved Palettes ({palettes.length})
        </h3>
        <div className="pal-list">
          {palettes.length === 0 ? (
            <p className="pal-list-empty">
              No palettes match your search criteria
            </p>
          ) : (
            palettes.map((palette) => (
              <div
                key={palette.id}
                className={`pal-card ${currentPalette?.id === palette.id ? "pal-card--active" : ""}`}
                role="button"
                tabIndex={0}
                aria-label={`Select palette: ${palette.name}`}
                onClick={() => onSelect(palette)}
                onKeyDown={(e) => {
                  if (e.key === "Enter" || e.key === " ") {
                    e.preventDefault();
                    onSelect(palette);
                  }
                }}
              >
                <div className="flex justify-between items-center mb-2">
                  <div className="flex-1 min-w-0">
                    <span className="font-medium text-gray-900 dark:text-gray-100">
                      {palette.name}
                    </span>
                    {palette.tags && palette.tags.length > 0 && (
                      <div className="flex flex-wrap gap-1 mt-1">
                        {palette.tags.slice(0, 3).map((tag) => (
                          <span
                            key={tag}
                            className="inline-flex items-center gap-0.5 px-1.5 py-0.5 bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 rounded text-[10px] font-medium"
                          >
                            <Tag className="w-2.5 h-2.5" />
                            {tag}
                          </span>
                        ))}
                        {palette.tags.length > 3 && (
                          <span className="text-[10px] text-gray-500 dark:text-gray-400">
                            +{palette.tags.length - 3}
                          </span>
                        )}
                      </div>
                    )}
                  </div>
                  <div className="flex items-center gap-2">
                    <button
                      type="button"
                      onClick={async (e) => {
                        e.stopPropagation();
                        if (palette.id) {
                          const ok = await confirm({
                            title: 'Delete palette?',
                            message: `Delete palette "${palette.name}"? This action cannot be undone.`,
                            danger: true,
                          });
                          if (ok) {
                            onDelete(palette.id);
                          }
                        }
                      }}
                      className="text-red-600 hover:text-red-700 dark:text-red-400"
                      aria-label={`Delete palette ${palette.name}`}
                      title="Delete palette"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </div>
                <div className="flex gap-1">
                  {palette.colors.slice(0, 8).map((color, i) => (
                    <div
                      key={i}
                      className="w-6 h-6 rounded border border-gray-200 dark:border-gray-600"
                      style={{ backgroundColor: color }}
                    />
                  ))}
                  {palette.colors.length > 8 && (
                    <span className="text-xs text-gray-500 dark:text-gray-400 flex items-center">
                      +{palette.colors.length - 8}
                    </span>
                  )}
                </div>
              </div>
            ))
          )}
        </div>
      </div>
    </div>
  );
}
